import {
    convertNullableDateTimeToUkgUserDateTime,
    convertRequestPeriodEndDate,
} from '@commonClient2/services/ukgDateTime';
import { BidProfileStatus } from '../../../Enums';
import { simplifyRebidParents } from '../BidProfileModelMapper';

const BriefBidProfileModelBuilder = {
    /**
     * @description Updates bid information in IBriefBaseProfile (from the store) BE response
     * @description Used to updated bidding list after manager saves step 1
     * @param {BidProfile.IBriefBaseProfile} original
     * @param {ApiManager.BriefBidProfilePresentationModel | ApiManager.BriefBidProfileDraftPresentationModel} updated
     * @returns {BidProfile.IBriefBaseProfile}
     */
    updateBidInformationInBriefProfile(original, updated) {
        return {
            ...original,
            name: updated.name,
            description: updated.description,
            isManagementAllowed: updated.isManagementAllowed,
            isViewAllowed: updated.isViewAllowed,
            isCreationAllowed: updated.isCreationAllowed,
            status: updated.status,
            scheduleSource: updated.scheduleSource,
            bidProcessType: updated.bidProcessType,
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(updated.lastUpdate) || new Date(0),
        };
    },
    /**
     * @description Updates schedule templates in IBriefBaseProfile (from the store) BE response
     * @description Used to updated bidding list after manager saves step 2
     * @param {BidProfile.IBriefBaseProfile} original
     * @param {ApiManager.IBidProfilePresentationModel | ApiManager.IBidProfileDraftPresentationModel} updated
     * @param {boolean} isDraft
     * @returns {BidProfile.IBriefBaseProfile}
     */
    updateScheduleTemplatesInBriefProfile(original, updated, isDraft) {
        return {
            ...original,
            // @ts-ignore
            status: isDraft ? BidProfileStatus.Draft : updated.status,
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(updated.lastUpdate) || new Date(0),
        };
    },
    /**
     * @description Updates employees in IBriefBaseProfile (from the store) BE response
     * @description Used to updated bidding list after manager saves step 3
     * @param {BidProfile.IBriefBaseProfile} original
     * @param {ApiManager.IBidProfilePresentationModel | ApiManager.IBidProfileDraftPresentationModel} updated
     * @param {boolean} isDraft
     * @param {{
     *  hyperfinds: ManagerAppData.IHyperfind[],
     * }} storeData
     * @returns {BidProfile.IBriefBaseProfile}
     */
    updateEmployeesInBriefProfile(original, updated, isDraft, storeData) {
        const hyperfind = storeData.hyperfinds.find((x) => x.value === updated.employeeInfos.hyperfindId);
        const hyperfindLabel = hyperfind ? hyperfind.label : '';
        return {
            ...original,
            // @ts-ignore
            status: isDraft ? BidProfileStatus.Draft : updated.status,
            hyperfindLabel,
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(updated.lastUpdate) || new Date(0),
            rankedEmployees: updated.employeeInfos.rankedEmployees,
        };
    },
    /**
     * @description Updates scheduling and visiblity in IBriefBaseProfile (from the store) BE response
     * @description Used to updated bidding list after manager saves step 4
     * @param {BidProfile.IBriefBaseProfile} original
     * @param {ApiManager.BriefBidProfilePresentationModel | ApiManager.BriefBidProfileDraftPresentationModel} updated
     * @returns {BidProfile.IBriefBaseProfile}
     */
    updateScheduleInBriefProfile(original, updated) {
        return {
            ...original,
            status: updated.status,
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(updated.requestPeriodStartDate),
            requestPeriodEndDate: convertRequestPeriodEndDate(updated.requestPeriodEndDate),
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(updated.submissionPeriodStartDate),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(updated.submissionPeriodEndDate),
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(updated.lastUpdate) || new Date(0),
            isForeverRequestPeriod: updated.isForeverRequestPeriod,
        };
    },
    /**
     * @description Transforms (BE) ApiManager.BriefBidProfilePresentationModel to (FE) BidProfile.IBriefBaseProfile typing
     * @description Used to updated bidding list when page is first loaded
     * @param {ApiManager.BriefBidProfilePresentationModel} profileResponse
     * @param {{
     *  hyperfinds: ManagerAppData.IHyperfind[],
     * }} storeData
     * @returns {BidProfile.IBriefBaseProfile}
     */
    fromBriefBidProfilePresentationModel(profileResponse, storeData) {
        // @ts-ignore - x.value can never be null
        const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
        return {
            ...profileResponse,
            id: profileResponse.profileId,
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(profileResponse.lastUpdate) || new Date(0),
            hyperfindLabel: profileResponse.employeeHyperfindId ? hyperfindDictionary[profileResponse.employeeHyperfindId] : '',
            rebidParents: simplifyRebidParents(
                profileResponse.rebidParents,
                { isCreationAllowed: profileResponse.isCreationAllowed, isManagementAllowed: profileResponse.isManagementAllowed },
                hyperfindDictionary,
            ),
            rankedEmployees: profileResponse.rankedEmployees,
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.submissionPeriodStartDate),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.submissionPeriodEndDate),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.requestPeriodStartDate),
            requestPeriodEndDate: convertRequestPeriodEndDate(profileResponse.requestPeriodEndDate),
        };
    },

    /**
     * @description Transforms (BE) ApiManager.BriefBidProfileDraftPresentationModel to (FE) BidProfile.IBriefBidProfile typing
     * @description Used to updated bidding list when page is first loaded
     * @param {ApiManager.BriefBidProfileDraftPresentationModel} profileResponse
     * @param {{
     *  hyperfinds: ManagerAppData.IHyperfind[],
     * }} storeData
     * @returns {BidProfile.IBriefBaseProfile}
     */
    fromBriefBidProfileDraftPresentationModel(profileResponse, storeData) {
        // @ts-ignore - x.value can never be null
        const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
        return {
            ...profileResponse,
            id: profileResponse.draftId,
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(profileResponse.lastUpdate) || new Date(0),
            hyperfindLabel: profileResponse.employeeHyperfindId ? hyperfindDictionary[profileResponse.employeeHyperfindId] : '',
            rebidParents: simplifyRebidParents(
                profileResponse.rebidParents,
                { isCreationAllowed: profileResponse.isCreationAllowed, isManagementAllowed: profileResponse.isManagementAllowed },
                hyperfindDictionary,
            ),
            rankedEmployees: profileResponse.rankedEmployees,
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.submissionPeriodStartDate),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.submissionPeriodEndDate),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(profileResponse.requestPeriodStartDate),
            requestPeriodEndDate: convertRequestPeriodEndDate(profileResponse.requestPeriodEndDate),
        };
    },

    /**
     * @description Transforms (BE) ApiManager.IBidProfilePresentationModel to (FE) BidProfile.IBriefBidProfile typing
     * @description Used when action returns full bid profile and bidding list needs to be updated (Delete rebid)
     * @param {ApiManager.IBidProfilePresentationModel} profileResponse
     * @param {{
     *  hyperfinds: ManagerAppData.IHyperfind[],
     * }} storeData
     * @returns {BidProfile.IBriefBaseProfile}
     */
    fromBidProfilePresentationModel(profileResponse, storeData) {
        // @ts-ignore - x.value can never be null
        const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
        const { hyperfindId } = profileResponse.employeeInfos;
        return {
            id: profileResponse.profileId,
            name: profileResponse.bidProfileInformation.name,
            description: profileResponse.bidProfileInformation.description,
            bidSetupId: profileResponse.bidProfileInformation.bidSetupId,
            status: profileResponse.status,
            scheduleSource: profileResponse.bidProfileInformation.scheduleSource,
            bidProcessType: profileResponse.bidProfileInformation.bidProcessType,
            isCreationAllowed: profileResponse.isCreationAllowed,
            isManagementAllowed: profileResponse.isManagementAllowed,
            isRebidAllowed: profileResponse.isRebidAllowed,
            isReImportNeeded: false, // TODO talk to Nat to know if this is still needed
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(profileResponse.lastUpdate) || new Date(0),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(
                profileResponse.schedulingAndVisibility.requestPeriodStartDate,
            ),
            requestPeriodEndDate: convertRequestPeriodEndDate(
                profileResponse.schedulingAndVisibility.requestPeriodEndDate
            ),
            isForeverRequestPeriod: profileResponse.schedulingAndVisibility.isForeverRequestPeriod,
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(
                profileResponse.schedulingAndVisibility.submissionPeriodStartDate,
            ),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(
                profileResponse.schedulingAndVisibility.submissionPeriodEndDate,
            ),
            employeeHyperfindId: hyperfindId,
            hyperfindLabel: hyperfindId ? hyperfindDictionary[hyperfindId] : '',
            rebidParents: simplifyRebidParents(
                profileResponse.rebidParents,
                { isCreationAllowed: profileResponse.isCreationAllowed, isManagementAllowed: profileResponse.isManagementAllowed },
                hyperfindDictionary,
            ),
            rankedEmployees: profileResponse.employeeInfos.rankedEmployees,
            rebidNumber: profileResponse.rebidNumber,
        };
    },
    /**
     * @description Transforms (BE) ApiManager.IBidProfileDraftPresentationModel to (FE) BidProfile.IBriefBidProfile typing
     * @description Used when action returns full bid profile draft and bidding list needs to be updated (Duplicate bid)
     * @param {ApiManager.IBidProfileDraftPresentationModel} draftResponse
     * @param {{
     *  hyperfinds: ManagerAppData.IHyperfind[],
     * }} storeData
     * @returns {BidProfile.IBriefBaseProfile}
     */
    fromBidProfileDraftPresentationModel(draftResponse, storeData) {
        // @ts-ignore - x.value can never be null
        const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
        const { hyperfindId } = draftResponse.employeeInfos;
        return {
            id: draftResponse.draftId,
            name: draftResponse.bidProfileInformation.name,
            description: draftResponse.bidProfileInformation.description,
            status: BidProfileStatus.Draft,
            bidSetupId: draftResponse.bidProfileInformation.bidSetupId,
            scheduleSource: draftResponse.bidProfileInformation.scheduleSource,
            bidProcessType: draftResponse.bidProfileInformation.bidProcessType,
            isCreationAllowed: draftResponse.isCreationAllowed,
            isManagementAllowed: draftResponse.isManagementAllowed,
            isRebidAllowed: draftResponse.isRebidAllowed,
            isReImportNeeded: false, // TODO talk to Nat to know if this is still needed
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(draftResponse.lastUpdate) || new Date(0),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(
                draftResponse.schedulingAndVisibility.requestPeriodStartDate,
            ),
            requestPeriodEndDate: convertRequestPeriodEndDate(
                draftResponse.schedulingAndVisibility.requestPeriodEndDate
            ),
            isForeverRequestPeriod: draftResponse.schedulingAndVisibility.isForeverRequestPeriod,
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(
                draftResponse.schedulingAndVisibility.submissionPeriodStartDate,
            ),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(
                draftResponse.schedulingAndVisibility.submissionPeriodEndDate,
            ),
            employeeHyperfindId: hyperfindId,
            hyperfindLabel: hyperfindId ? hyperfindDictionary[hyperfindId] : '',
            rebidParents: simplifyRebidParents(
                draftResponse.rebidParents,
                { isCreationAllowed: draftResponse.isCreationAllowed, isManagementAllowed: draftResponse.isManagementAllowed },
                hyperfindDictionary,
            ),
            rankedEmployees: draftResponse.employeeInfos.rankedEmployees,
            rebidNumber: draftResponse.rebidNumber,
        };
    },
};

export default BriefBidProfileModelBuilder;
